'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  detectRegion,
  getConsent,
  saveConsent,
  createConsentRecord,
  logConsentAction,
  type ConsentRegion,
} from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

export default function CookieBanner() {
  const [region, setRegion] = useState<ConsentRegion>('none');
  const [hasConsent, setHasConsent] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const detectedRegion = detectRegion();
    setRegion(detectedRegion);

    const existingConsent = getConsent();
    if (existingConsent) {
      setHasConsent(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const record = createConsentRecord(region, {
      analytics: true,
      marketing: true,
      preferences: true,
    });
    saveConsent(record);
    logConsentAction('accept_all', region);
    setHasConsent(true);
  };

  const handleRejectAll = () => {
    const record = createConsentRecord(region, {
      analytics: false,
      marketing: false,
      preferences: false,
    });
    saveConsent(record);
    logConsentAction('reject_all', region);
    setHasConsent(true);
  };

  const handleCCPADismiss = () => {
    const record = createConsentRecord(region, {
      analytics: true,
      marketing: false,
      preferences: true,
    });
    saveConsent(record);
    logConsentAction('dismiss_ccpa', region);
    setHasConsent(true);
  };

  // GDPR Banner
  if (region === 'gdpr' && !hasConsent) {
    return (
      <>
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-surface border-t border-border shadow-lg">
          <div className="mx-auto px-6 lg:px-12 py-6 max-w-7xl">
            <div className="flex flex-col gap-6">
              <div>
                <h2 className="font-headline text-lg font-semibold text-primary mb-3">
                  Cookie Consent
                </h2>
                <p className="font-body text-sm text-text-secondary mb-4">
                  We use cookies to enhance your experience, analyze site traffic, and enable marketing features.
                  By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or
                  learn more by visiting our{' '}
                  <Link href="/privacy" className="text-accent hover:underline">
                    Privacy Policy
                  </Link>
                  {' '}and{' '}
                  <Link href="/cookies" className="text-accent hover:underline">
                    Cookie Policy
                  </Link>
                  .
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={handleRejectAll}
                  className="px-6 py-2 font-body text-sm font-semibold text-primary border border-border rounded-sm hover:bg-surface-hover transition-colors duration-smooth"
                >
                  Reject All
                </button>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="px-6 py-2 font-body text-sm font-semibold text-primary border border-border rounded-sm hover:bg-surface-hover transition-colors duration-smooth"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2 font-body text-sm font-semibold text-primary-foreground bg-primary rounded-sm hover:bg-secondary transition-colors duration-smooth"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
        {showPreferences && (
          <ConsentPreferenceCenter
            region={region}
            onClose={() => setShowPreferences(false)}
            onSave={() => {
              setHasConsent(true);
              setShowPreferences(false);
            }}
          />
        )}
      </>
    );
  }

  // CCPA Banner
  if (region === 'ccpa' && !hasConsent) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-surface border-t border-border shadow-lg">
        <div className="mx-auto px-6 lg:px-12 py-6 max-w-7xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="font-body text-sm text-text-secondary flex-1">
              We respect your privacy. California residents can{' '}
              <Link href="/privacy#do-not-sell" className="text-accent hover:underline">
                opt out of the sale of personal information
              </Link>
              .
            </p>
            <button
              onClick={handleCCPADismiss}
              className="px-6 py-2 font-body text-sm font-semibold text-primary-foreground bg-primary rounded-sm hover:bg-secondary transition-colors duration-smooth whitespace-nowrap"
            >
              Got It
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
